/**
 * Institutional Kill-Zones (Smart Money Concepts / ICT-inspired).
 * All times in UTC. The user's local timezone is only for display, not for computation.
 *
 * Standard kill-zones (post-London-EST alignment):
 *   - Asia:       23:00 → 04:00 UTC  (Tokyo → early Sydney overlap)
 *   - London:     07:00 → 10:00 UTC  (London open + first 3h)
 *   - New York AM: 12:00 → 15:00 UTC  (NY open + first 3h)
 *   - New York PM: 17:30 → 20:00 UTC  (NY afternoon reversal window)
 *
 * Each zone crosses over multiple candles depending on the timeframe.
 * Rendered as adaptive boxes anchored to the opening candle high/low
 * and closing candle high/low, based on the directional bias.
 */

// Default palette — user can override via ColorSwatchPicker in theme menu.
export const DEFAULT_KILL_ZONE_COLORS = {
  asia:   { color: "rgba(139, 92, 246, 0.18)", stroke: "rgba(139, 92, 246, 0.65)" },
  london: { color: "rgba(56, 189, 248, 0.18)", stroke: "rgba(56, 189, 248, 0.7)" },
  ny_am:  { color: "rgba(34, 197, 94, 0.18)",  stroke: "rgba(34, 197, 94, 0.7)" },
  ny_pm:  { color: "rgba(245, 158, 11, 0.18)", stroke: "rgba(245, 158, 11, 0.7)" },
};

export const KILL_ZONES = [
  { id: "asia",   label: "Asia",   start: 23 * 60, end: (24 + 4) * 60 },
  { id: "london", label: "London", start:  7 * 60, end: 10 * 60 },
  { id: "ny_am",  label: "NY AM",  start: 12 * 60, end: 15 * 60 },
  { id: "ny_pm",  label: "NY PM",  start: 17 * 60 + 30, end: 20 * 60 },
];

/**
 * Return the minute-of-day (UTC) for a UNIX timestamp (seconds).
 * Handles the wrap-around cases (Asia crosses midnight UTC).
 */
export function utcMinuteOfDay(unixSec) {
  const d = new Date(unixSec * 1000);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/**
 * Check if a candle time (unix seconds) falls inside a given zone.
 */
export function isInZone(unixSec, zone) {
  const m = utcMinuteOfDay(unixSec);
  if (zone.end > 24 * 60) {
    // Wraps midnight
    return m >= zone.start || m < (zone.end - 24 * 60);
  }
  return m >= zone.start && m < zone.end;
}

/**
 * Compute contiguous kill-zone ranges from a candle series.
 * Each range is anchored to the opening candle & closing candle high/low
 * based on directional bias (bull = up, bear = down).
 * Returns [{zoneId, label, startTime, endTime, topOpen, bottomOpen, topClose, bottomClose, bias}, ...]
 */
export function computeKillZoneRanges(candles) {
  if (!candles || !candles.length) return [];
  const ranges = [];

  const getTime = (c) => {
    let t = c.time ?? c.timestamp ?? c.t;
    if (typeof t === "string") t = Math.floor(new Date(t).getTime() / 1000);
    else if (t > 1e12) t = Math.floor(t / 1000);
    return t;
  };

  for (const zone of KILL_ZONES) {
    let openIdx = null;
    for (let i = 0; i < candles.length; i++) {
      const t = getTime(candles[i]);
      const inside = isInZone(t, zone);
      if (inside && openIdx === null) {
        openIdx = i;
      } else if (!inside && openIdx !== null) {
        const closeIdx = i - 1;
        const openCandle = candles[openIdx];
        const closeCandle = candles[closeIdx];
        const bias = closeCandle.close >= openCandle.open ? "bull" : "bear";
        ranges.push({
          zoneId: zone.id,
          label: zone.label,
          startTime: getTime(openCandle),
          endTime: getTime(closeCandle),
          openHigh: openCandle.high,
          openLow: openCandle.low,
          closeHigh: closeCandle.high,
          closeLow: closeCandle.low,
          bias,
        });
        openIdx = null;
      }
    }
    if (openIdx !== null) {
      const closeIdx = candles.length - 1;
      const openCandle = candles[openIdx];
      const closeCandle = candles[closeIdx];
      const bias = closeCandle.close >= openCandle.open ? "bull" : "bear";
      ranges.push({
        zoneId: zone.id,
        label: zone.label,
        startTime: getTime(openCandle),
        endTime: getTime(closeCandle),
        openHigh: openCandle.high,
        openLow: openCandle.low,
        closeHigh: closeCandle.high,
        closeLow: closeCandle.low,
        bias,
      });
    }
  }
  return ranges;
}

/**
 * Detect institutional volume markers using Z-score.
 * Returns markers where volume Z-score exceeds `threshold` (default 2.0σ).
 * Each marker: { time, price, side ('accumulation' | 'distribution'), zScore }
 *
 *  - Accumulation: high volume + bullish close on that bar (or wick rejection lower)
 *  - Distribution: high volume + bearish close (or wick rejection higher)
 */
export function detectInstitutionalMarkers(candles, threshold = 1.5) {
  if (!candles || candles.length < 20) return [];
  const getTime = (c) => {
    let t = c.time ?? c.timestamp ?? c.t;
    if (typeof t === "string") t = Math.floor(new Date(t).getTime() / 1000);
    else if (t > 1e12) t = Math.floor(t / 1000);
    return t;
  };
  // Compute average true range for body normalization
  const bodies = candles.map((c) => Math.abs(c.close - c.open));
  const meanBody = bodies.reduce((a, b) => a + b, 0) / bodies.length;
  const stdBody = Math.sqrt(bodies.reduce((a, v) => a + (v - meanBody) ** 2, 0) / bodies.length) || meanBody || 1;

  const markers = [];
  for (let i = 5; i < candles.length; i++) {
    const c = candles[i];
    const body = Math.abs(c.close - c.open);
    const zBody = (body - meanBody) / stdBody;
    if (zBody < threshold) continue;
    // Direction = candle color (very predictable UX)
    const isBull = c.close > c.open;
    markers.push({
      time: getTime(c),
      price: isBull ? c.low : c.high,
      side: isBull ? "buy" : "sell",
      zScore: Number(zBody.toFixed(2)),
      candleIndex: i,
    });
  }
  return markers;
}
